/** NPC source window adapted from the existing PC twin; all rule kernels stay shared. */
import type { ItemFieldRow } from './item-field-reader';
import type { FromBaseRow } from './from-base-context';
type FromBasePlayerInput = FromBaseRow;
import { fromBaseRecord, fromBaseUuid } from './from-base-context';

import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createAbilityEssenceSourceCollector } from './ability-essence-sources';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { readInventoryField, readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';

type Source = { name: string; kind: string; href: string | null; adv_list: unknown[]; dis_list: unknown[]; mod_list?: unknown[] };
const containers = ['feat', 'passive_ability', 'status_effect_permanent'] as const;
const rows = (value: unknown): unknown[] => value !== null && typeof value === 'object' ? Object.values(value) : [];

/** Native skill_roll_mod cell, shared by source collection and its cache identity. */
export function readNpcSkillModifier(entry: unknown, value: (row: ItemFieldRow | null, key: string) => unknown) {
    const explicit = fromBaseRecord(entry) && fromBaseUuid(value(entry, 'skill'));
    const skill = explicit ? phpJsonString(value(entry as FromBaseRow, 'skill')) : readLayerListEntryUuid(entry).trim();
    let quantity = fromBaseRecord(entry) ? value(entry, 'qty') : typeof entry === 'string' ? entry.split('|', 2)[1] : null;
    if (!isPhpNumeric(quantity) && explicit) quantity = value(entry as FromBaseRow, 'mod');
    if (!explicit && fromBaseRecord(entry) && isPhpNumeric(quantity) && !phpJsonString(quantity).includes('.')) quantity = phpJsonInt(quantity);
    return { skill, mod: isPhpNumeric(quantity) ? roundPhpDecimal(phpJsonFloat(quantity), 0) : 1 };
}

/** Native full-skill source discovery. It consumes the constructed host, never
 * a PHP skill bucket, and leaves raw definitions and source order intact. */
export function constructNpcSkillSourcesFromBase(base: FromBasePlayerInput, metadata: import('./from-base-player-resources').FromBasePlayerResourceMetadata, host: FromBaseRow) {
    const { readers, definition, deps } = createFromBaseNpcGrantContext(base, metadata, host, 'integer-rollup-full-skills');
    const value = readers.layerValue, field = readers.itemField;
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const root = (key: string) => rows(host[key]);
    const conditions = createAbilityConditionEvaluator(deps);
    const level = createAbilityEssenceSourceCollector({ ...deps, metadata: metadata.preliminary }).levelGate(host);
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId, resolveCarrierRef: definition });
    const trigger = (row: FromBaseRow): boolean => [...Object.keys(row), ...Object.keys(fromBaseRecord(row.item_layer_types) ? row.item_layer_types : {})].some(key => key.includes('trigger'));
    const resolve = (entry: unknown, self: boolean): FromBaseRow | null => {
        const reference = fromBaseRecord(entry) ? entry.item_arr ?? entry.item_id ?? (self ? entry : null) : self ? entry : null;
        return fromBaseRecord(reference) ? reference : fromBaseUuid(reference) ? definition(reference) as FromBaseRow : null;
    };
    const idOf = (entry: unknown) => readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition });
    const rawIds = (container: string) => new Set(root(container).filter(fromBaseRecord).map(entry =>
        phpJsonString(field(fromBaseRecord(entry.item_arr) ? entry.item_arr : {}, 'item_id') ?? idOf(entry))));
    const carrierOf = new Map<string, string>();
    for (const provider of providers.providers(host)) for (const container of containers) for (const entry of entries(provider, container)) {
        const key = `${container}:${idOf(entry).toLowerCase()}`;
        if (!carrierOf.has(key)) carrierOf.set(key, readNodeItemId(provider) ?? '');
    }
    const allowed = (row: FromBaseRow, entry: unknown, carrier = '', gateLevel = false): boolean => {
        const applied = fromBaseRecord(entry) && value(entry, 'built_from_item_type') === 'status_effect';
        return (!gateLevel || applied || !level || level({ source: carrier }, row))
            && conditions.check(row, host) && (!trigger(row) || applied);
    };
    const source = (row: FromBaseRow, kind: string, fallback: string, entry: ItemFieldRow | null = null): Source => {
        const href = field(row, 'item_id') ?? value(row, 'ability_id') ?? field(entry, 'item_id');
        return { name: phpJsonString(field(row, 'item_name') ?? value(row, 'ability_name') ?? field(entry, 'item_name') ?? fallback), kind,
            href: href == null || href === '' ? null : phpJsonString(href),
            adv_list: entries(row, 'skill_roll_adv'), dis_list: entries(row, 'skill_roll_dis'), mod_list: entries(row, 'skill_roll_mod') };
    };
    const carrier: Source[] = [];
    for (const container of containers) {
        const raw = rawIds(container);
        for (const entry of entries(host, container)) {
            const row = resolve(entry, true);
            if (!row) continue;
            const id = phpJsonString(field(row, 'item_id') ?? idOf(entry));
            if (!id || raw.has(id) || !allowed(row, entry, carrierOf.get(`${container}:${id.toLowerCase()}`), true)) continue;
            const item = source(row, container === 'status_effect_permanent' ? 'status_effect' : 'passive_ability',
                container === 'feat' ? 'Feat' : container === 'status_effect_permanent' ? 'Status Effect' : 'Ability');
            if (item.adv_list.length || item.dis_list.length || item.mod_list!.length) carrier.push(item);
        }
    }
    const seen = new Set([...containers.flatMap(container => [...rawIds(container)]), ...carrier.map(item => item.href ?? '')]);
    const seenFeats = new Set<string>();
    for (const entry of [...root('feat'), ...entries(host, 'feat')]) {
        const row = resolve(entry, true);
        if (!row) continue;
        const id = phpJsonString(field(row, 'item_id') ?? '');
        if (!id || seenFeats.has(id)) continue;
        seenFeats.add(id);
        // Native parent eligibility does not exclude triggers; child eligibility does.
        if ((level && !level({ source: carrierOf.get(`feat:${id.toLowerCase()}`) ?? '' }, row)) || !conditions.check(row, host)) continue;
        for (const grant of entries(row, 'passive_ability')) {
            const child = resolve(grant, true);
            if (!child) continue;
            const childId = phpJsonString(field(child, 'item_id') ?? '');
            if (!childId || seen.has(childId)) continue;
            seen.add(childId);
            if (!allowed(child, {}, '', true)) continue;
            const item = source(child, 'passive_ability', 'Ability');
            if (item.adv_list.length || item.dis_list.length || item.mod_list!.length) carrier.push(item);
        }
    }
    const ownLayers = fromBaseRecord(host.item_layer_types) ? host.item_layer_types : null;
    // Use an isolated reader surface for authored-only layers, excluding provider defaults.
    const authored = (key: string): ItemFieldRow | null => ownLayers && Object.prototype.hasOwnProperty.call(ownLayers, key)
        ? { item_layer_types: { [key]: ownLayers[key] } } : null;
    const authoredValue = (key: string): unknown => ownLayers && typeof ownLayers[key] === 'object' && ownLayers[key] !== null
        ? entries(authored(key), key) : value(authored(key), key);
    const hostId = field(host, 'item_id');
    const hostSource = ownLayers ? { adv: authoredValue('skill_roll_adv'), dis: authoredValue('skill_roll_dis'),
        name: phpJsonString(field(host, 'item_name') ?? 'Self'), href: hostId == null || hostId === '' ? null : phpJsonString(hostId) } : null;
    const direct: Source[] = [];
    for (const container of containers) for (const entry of root(container)) {
        if (!fromBaseRecord(entry) || value(entry, 'inventory_source') !== null || entries(entry, 'inventory_source').length) continue;
        const row = resolve(entry, false);
        if (!row || !allowed(row, entry)) continue;
        const item = source(row, container === 'status_effect_permanent' ? 'status_effect' : 'passive_ability',
            container === 'feat' ? 'Feat' : container === 'status_effect_permanent' ? 'Status Effect' : 'Ability', entry);
        if (item.adv_list.length || item.dis_list.length || item.mod_list!.length) direct.push(item);
    }
    const inventory: Source[] = [], modInventory: Source[] = [], slots = new Set<string>();
    const computed = fromBaseRecord(host.computed) ? host.computed : {};
    const creature = fromBaseRecord(computed.creature) ? computed.creature : {};
    for (const row of [...root('inventory').filter(row => fromBaseRecord(row) && readItemUuidSlot(readInventoryField(row, 'equipped'))), ...root('inventory_equipped_arrs')]) {
        if (!fromBaseRecord(row)) continue;
        const slot = readItemUuidSlot(value(row, 'equipment_slot')).toUpperCase();
        const modAllowed = !slot || !slots.has(slot);
        if (slot) slots.add(slot);
        const name = phpJsonString(field(row, 'item_name') ?? 'Equipment');
        const identity = readInventoryField(row, 'inventory_id') ?? field(row, 'item_id');
        const href = identity == null || identity === '' ? null : phpJsonString(identity);
        const add = (view: FromBaseRow, label: string): void => {
            const item = { ...source(view, 'equipment', 'Equipment'), name: label, href };
            if (item.adv_list.length || item.dis_list.length) inventory.push(item);
            if (modAllowed && item.mod_list!.length) modInventory.push(item);
        };
        add(row, name);
        for (const enchantment of rows(row.enchantment)) {
            const view = fromBaseRecord(enchantment) && fromBaseRecord(enchantment.item_arr) ? enchantment.item_arr : enchantment;
            if (fromBaseRecord(view)) add(view, `${name} — enchant`);
        }
        if (!phpJsonTruthy(creature.abilities)) {
            const rowComputed = fromBaseRecord(row.computed) ? row.computed : {};
            const enchantment = fromBaseRecord(rowComputed.enchantment) ? rowComputed.enchantment : {};
            for (const bucket of rows(enchantment.abilities)) for (const entry of rows(bucket)) {
                if (!fromBaseRecord(entry) || !fromBaseRecord(entry.item_arr) || value(entry, 'passive_conditions_active') === false) continue;
                const ability = entry.item_arr;
                if (!conditions.check(ability, host) || trigger(ability)) continue;
                add(ability, `${name} — ${phpJsonString(field(ability, 'item_name') ?? 'Enchantment')}`);
            }
        }
    }
    const statuses: Source[] = [];
    for (const row of Object.values(readCreaturePayloadStatuses(host, value, definition))) {
        if (!fromBaseRecord(row)) continue;
        const view = fromBaseRecord(row.status_arr) ? row.status_arr : row;
        if (field(view, 'item_type') === 'creature') continue;
        const identity = value(row, 'status_id') ?? field(row, 'item_id');
        const item = { ...source(view, 'status_effect', 'Status Effect'), name: phpJsonString(field(row, 'item_name') ?? 'Status Effect'),
            href: identity == null || identity === '' ? null : phpJsonString(identity) };
        if (item.adv_list.length || item.dis_list.length || item.mod_list!.length) statuses.push(item);
    }
    const advOnly = (items: Source[]) => items.filter(item => item.adv_list.length || item.dis_list.length).map(({ mod_list: _mods, ...item }) => item);
    const mods: { skill: string; mod: number; label: string; kind: string; href: string | null }[] = [];
    const pushMods = (list: unknown[], label: string, kind: string, href: string | null): void => {
        for (const entry of list) {
            const { skill, mod } = readNpcSkillModifier(entry, value);
            if (fromBaseUuid(skill) && mod !== 0) mods.push({ skill, mod, label, kind, href });
        }
    };
    if (hostSource && ownLayers && typeof ownLayers.skill_roll_mod === 'object' && ownLayers.skill_roll_mod !== null)
        pushMods(entries(authored('skill_roll_mod'), 'skill_roll_mod'), hostSource.name, 'adv_dis', hostSource.href);
    for (const item of [...direct, ...carrier, ...modInventory, ...statuses]) pushMods(item.mod_list ?? [], item.name, item.kind, item.href);
    return { carrier, static: { host: hostSource, containers: advOnly(direct), inventory: advOnly(inventory), statuses: advOnly(statuses) }, mods };
}
